import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import Popover from '../../components/molecules/Popover';
import AgentActivityHistoryList from './AgentActivityHistoryList';
import { AGENT_STATE, THINKING_FLICKER_DELAY_MS, formatDuration } from '../constants';
import css from './AgentActivity.css';

const POPOVER_ANCHOR_ORIGIN = { vertical: 'top', horizontal: 'left' };
const POPOVER_TRANSFORM_ORIGIN = { vertical: 'bottom', horizontal: 'left' };

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.AGENT_ACTIVITY';

const useDebouncedAgentState = (state, currentTool) => {
    const [displayState, setDisplayState] = useState(state);
    const [displayTool, setDisplayTool] = useState(currentTool);
    const flickerTimer = useRef(null);

    useEffect(() => {
        const clearFlickerTimer = () => {
            if (flickerTimer.current) {
                clearTimeout(flickerTimer.current);
                flickerTimer.current = null;
            }
        };

        if (state === AGENT_STATE.DONE) {
            clearFlickerTimer();
            setDisplayState(AGENT_STATE.DONE);
            return undefined;
        }
        if (state === AGENT_STATE.RUNNING) {
            clearFlickerTimer();
            setDisplayState(AGENT_STATE.RUNNING);
            setDisplayTool(currentTool);
            return undefined;
        }
        if (displayState === AGENT_STATE.RUNNING) {
            clearFlickerTimer();
            flickerTimer.current = setTimeout(() => {
                setDisplayState(AGENT_STATE.THINKING);
                flickerTimer.current = null;
            }, THINKING_FLICKER_DELAY_MS);
            return clearFlickerTimer;
        }
        setDisplayState(AGENT_STATE.THINKING);
        return undefined;
    }, [state, currentTool, displayState]);

    return { displayState, displayTool };
};

const StepsChip = ({ stepCount, expanded }) => (
    <>
        <span className={css.stepsChip}>
            <Translate
                id={`${TRANSLATION_PREFIX}.${stepCount === 1 ? 'STEPS_COUNT_ONE' : 'STEPS_COUNT_MANY'}`}
                data={{ count: stepCount }}
            />
        </span>
        <Icon
            name="chevron-down"
            size={IconSizes.X_SMALL}
            className={classNames(css.trailingChevron, { [css.chevronOpen]: expanded })}
        />
    </>
);

StepsChip.propTypes = {
    stepCount: PropTypes.number.isRequired,
    expanded: PropTypes.bool.isRequired,
};

const ThinkingContent = () => (
    <>
        <Icon name="loader-2" size={IconSizes.X_SMALL} className={css.spinner} />
        <span className={css.thinkingLabel}>
            <Translate id={`${TRANSLATION_PREFIX}.THINKING_LABEL`} />
        </span>
    </>
);

const RunningContent = ({ tool }) => (
    <>
        <Icon name="code" size={IconSizes.X_SMALL} className={css.toolBadge} />
        <span className={css.toolName}>
            {tool?.name || <Translate id={`${TRANSLATION_PREFIX}.WORKING_FALLBACK`} />}
        </span>
        <Icon name="loader-2" size={IconSizes.X_SMALL} className={css.spinner} />
    </>
);

RunningContent.propTypes = {
    tool: PropTypes.shape({ name: PropTypes.string }),
};

const DoneContent = ({ stepCount, totalDurationMs, expanded }) => (
    <>
        <Icon name="check" size={IconSizes.X_SMALL} className={css.doneCheckBadge} variant={IconVariants.DARK} />
        <span className={css.doneLabel}>
            <Translate id={`${TRANSLATION_PREFIX}.ANALYSIS_COMPLETE`} />
        </span>
        <span className={css.doneSeparator} aria-hidden="true" />
        <span className={css.doneMeta}>
            <Translate
                id={`${TRANSLATION_PREFIX}.${stepCount === 1 ? 'STEPS_COUNT_ONE' : 'STEPS_COUNT_MANY'}`}
                data={{ count: stepCount }}
            />
        </span>
        <span className={css.doneSeparator} aria-hidden="true" />
        <span className={css.doneMeta}>{formatDuration(totalDurationMs)}</span>
        <Icon
            name="chevron-down"
            size={IconSizes.X_SMALL}
            className={classNames(css.trailingChevron, { [css.chevronOpen]: expanded })}
        />
    </>
);

DoneContent.propTypes = {
    stepCount: PropTypes.number.isRequired,
    totalDurationMs: PropTypes.number.isRequired,
    expanded: PropTypes.bool.isRequired,
};

const AgentActivity = ({ state, currentTool, history, totalDurationMs }) => {
    const { displayState, displayTool } = useDebouncedAgentState(state, currentTool);
    const [expanded, setExpanded] = useState(false);
    const capsuleRef = useRef(null);
    const prevDisplayState = useRef(displayState);

    useEffect(() => {
        if (prevDisplayState.current !== AGENT_STATE.DONE && displayState === AGENT_STATE.DONE) {
            setExpanded(false);
        }
        prevDisplayState.current = displayState;
    }, [displayState]);

    useEffect(() => {
        if (!expanded) return undefined;
        const handlePointerDown = event => {
            if (event.target.closest(`.${css.popoverRoot}`)) return;
            if (capsuleRef.current && capsuleRef.current.contains(event.target)) return;
            setExpanded(false);
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [expanded]);

    const stepCount = history.length;
    const hasHistory = stepCount > 0;
    const isDone = displayState === AGENT_STATE.DONE;
    const toggleExpanded = () => hasHistory && setExpanded(prev => !prev);

    return (
        <div className={css.capsuleWrap}>
            <button
                ref={capsuleRef}
                type="button"
                className={css.capsule}
                onClick={toggleExpanded}
                disabled={!hasHistory}
                aria-expanded={hasHistory ? expanded : undefined}
            >
                {displayState === AGENT_STATE.THINKING && <ThinkingContent />}
                {displayState === AGENT_STATE.RUNNING && <RunningContent tool={displayTool} />}
                {isDone && <DoneContent stepCount={stepCount} totalDurationMs={totalDurationMs} expanded={expanded} />}
                {!isDone && hasHistory && <StepsChip stepCount={stepCount} expanded={expanded} />}
            </button>

            <Popover
                showPopover={hasHistory && expanded}
                anchorEl={capsuleRef.current}
                anchorOrigin={POPOVER_ANCHOR_ORIGIN}
                transformOrigin={POPOVER_TRANSFORM_ORIGIN}
                className={css.popover}
                rootClassName={css.popoverRoot}
                disableScrollLock
                hideBackdrop
                transitionDuration={0}
                PaperProps={{ className: css.popoverPaper }}
            >
                <div className={css.popoverInner}>
                    <AgentActivityHistoryList steps={history} />
                </div>
            </Popover>
        </div>
    );
};

AgentActivity.propTypes = {
    state: PropTypes.oneOf(Object.values(AGENT_STATE)).isRequired,
    currentTool: PropTypes.shape({
        name: PropTypes.string,
    }),
    history: PropTypes.array.isRequired,
    totalDurationMs: PropTypes.number,
};

AgentActivity.defaultProps = {
    currentTool: null,
    totalDurationMs: 0,
};

export default AgentActivity;
